import { JOURS_FERIES_GABON } from '@shared/constants/JoursFeries';
import type { JourFerie } from '@shared/constants/JoursFeries';

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function isoUtc(y: number, month1: number, day: number): string {
  return `${y}-${pad(month1)}-${pad(day)}`;
}

/** Dimanche de Pâques (algorithme de Meeus / Jones / Butcher, calendrier grégorien). */
export function dimanchePaques(annee: number): Date {
  const a = annee % 19;
  const b = Math.floor(annee / 100);
  const c = annee % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(annee, month - 1, day));
}

function addDaysUtc(base: Date, days: number): Date {
  return new Date(base.getTime() + days * 86_400_000);
}

function lastSundayOfMay(annee: number): string {
  const last = new Date(Date.UTC(annee, 5, 0));
  const offset = last.getUTCDay();
  last.setUTCDate(last.getUTCDate() - offset);
  return last.toISOString().slice(0, 10);
}

/** Fête des Pères : 3ᵉ dimanche de juin (usage courant). */
function thirdSundayOfJune(annee: number): string {
  const first = new Date(Date.UTC(annee, 5, 1));
  const toSunday = (7 - first.getUTCDay()) % 7;
  const third = 1 + toSunday + 14;
  return isoUtc(annee, 6, third);
}

export class CalendrierService {
  /** Jours fériés fixes d’une année (1er janvier, 1er mai, 17 août, Toussaint, Noël). */
  static joursFeriesFixes(annee: number): { date: string; nom: string }[] {
    return JOURS_FERIES_GABON.filter((j) => !j.mobile).map((j) => ({
      date: isoUtc(annee, j.mois, j.jour),
      nom: j.nom,
    }));
  }

  /** Fériés chrétiens mobiles + fêtes des mères / pères. Aïd non daté (calendrier lunaire). */
  static joursFeriesMobiles(annee: number): { date: string; nom: string }[] {
    const paques = dimanchePaques(annee);
    return [
      { date: addDaysUtc(paques, 1).toISOString().slice(0, 10), nom: 'Lundi de Pâques' },
      { date: addDaysUtc(paques, 39).toISOString().slice(0, 10), nom: 'Ascension' },
      { date: addDaysUtc(paques, 50).toISOString().slice(0, 10), nom: 'Lundi de Pentecôte' },
      { date: lastSundayOfMay(annee), nom: 'Fête des Mères' },
      { date: thirdSundayOfJune(annee), nom: 'Fête des Pères' },
    ];
  }

  static joursFeries(annee: number): { date: string; nom: string }[] {
    return [...this.joursFeriesFixes(annee), ...this.joursFeriesMobiles(annee)];
  }

  /**
   * Jours ouvrables lun–sam, hors fériés, sur une période éventuellement à cheval
   * sur deux années civiles.
   */
  static joursOuvrables(date_debut: string, date_fin: string): number {
    const debut = parseIsoUtc(date_debut);
    const fin = parseIsoUtc(date_fin);
    if (!debut || !fin || fin < debut) return 0;

    const feries = new Set<string>();
    for (let y = debut.getUTCFullYear(); y <= fin.getUTCFullYear(); y += 1) {
      for (const f of this.joursFeries(y)) feries.add(f.date);
    }

    let count = 0;
    const cur = new Date(debut.getTime());
    while (cur.getTime() <= fin.getTime()) {
      const day = cur.getUTCDay();
      const iso = cur.toISOString().slice(0, 10);
      if (day !== 0 && !feries.has(iso)) count += 1;
      cur.setUTCDate(cur.getUTCDate() + 1);
    }
    return count;
  }

  static get jours(): readonly JourFerie[] {
    return JOURS_FERIES_GABON;
  }
}

function parseIsoUtc(raw: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw.trim())) return null;
  const d = new Date(`${raw.trim()}T00:00:00.000Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}
